import React, { useMemo, useState } from 'react'
import styled from '@emotion/styled'
import {
  Section,
  FormItem,
  Space,
  Card,
  Radio as StyledRadio
} from 'components/Common'
import { StepStatus } from 'hooks/useStep'
import { Button, Row, Col, Form, Tooltip } from 'antd'
import Radio from 'antd/lib/radio'
import { useNavigate } from 'react-router-dom'
import { LightspeedBusiness } from './types'
import { DeleteButton } from './styles'
import RemoveIntegrationModal from './RemoveIntegrationModal'

interface BusinessSectionProps {
  businesses: LightspeedBusiness[]
  selectedBusiness: LightspeedBusiness | null
  stepStatus: StepStatus
  onFinish: (business: LightspeedBusiness | null) => void
  onDisconnect: () => void
  canDisconnect: boolean
  onRemoveIntegration: () => void
}

const BusinessSection = ({
  businesses,
  selectedBusiness,
  stepStatus,
  onFinish,
  onDisconnect,
  canDisconnect,
  onRemoveIntegration
}: BusinessSectionProps) => {
  const navigate = useNavigate()
  const [form] = Form.useForm()

  const [isRemoveIntegrationModalOpen, setIsRemoveIntegrationModalOpen] =
    useState(false)

  const initialValues = useMemo(() => {
    return {
      businessId: businesses.length === 1 ? businesses[0].id : null
    }
  }, [businesses])

  if (!businesses.length) {
    return <></>
  }

  const handleDisconnect = () => {
    onDisconnect()
  }

  if (stepStatus === StepStatus.completed) {
    return (
      <Section
        title={
          <>
            Connected Business:{' '}
            <BusinessName>{selectedBusiness?.name}</BusinessName>
          </>
        }
        description='You will be able to select locations and menus from this business in the next steps. To change the business, you must remove all store connections.'
        stepStatus={stepStatus}
        stepNumber={1}
        action={
          <Tooltip
            title={
              !canDisconnect &&
              'To change the business, you must remove all store connections.'
            }
          >
            <Actions inactive={!canDisconnect}>
              <DeleteButton onClick={handleDisconnect}>
                <span>Disconnect</span>
              </DeleteButton>
            </Actions>
          </Tooltip>
        }
      />
    )
  }

  const handleFinish = (values: { businessId: string }) => {
    const selectedBusiness = businesses.find(
      (business) => business.id === values.businessId
    )

    onFinish(selectedBusiness || null)
  }

  const handleRemoveIntegration = () => {
    onRemoveIntegration()
  }

  return (
    <Root>
      <Form
        form={form}
        initialValues={initialValues}
        onFinish={handleFinish}
        scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
        requiredMark={false}
      >
        <Section
          title='Choose a business to connect'
          description='You will be able to select locations and menus from this business in the next steps. To change the business, you must remove all store connections.'
          stepStatus={stepStatus}
          stepNumber={1}
          action={
            <Actions>
              <DeleteButton
                onClick={() => setIsRemoveIntegrationModalOpen(true)}
              >
                <span>Disconnect</span>
              </DeleteButton>
            </Actions>
          }
        >
          <FormItem name='businessId'>
            <Radio.Group>
              <Space direction='vertical' align='start' gap={8}>
                {businesses.map((business) => (
                  <StyledRadio value={business.id} key={business.id}>
                    <strong>{business.name}</strong>
                  </StyledRadio>
                ))}
              </Space>
            </Radio.Group>
          </FormItem>
        </Section>
        <Card gridArea='save'>
          <Row className='_mb-0' justify='end' gutter={[24, 0]}>
            <Col>
              <Button
                type='ghost'
                htmlType='button'
                onClick={() => navigate('/integrations', { replace: true })}
              >
                Cancel
              </Button>
            </Col>

            <Col>
              <Button
                type='primary'
                htmlType='submit'
                disabled={false}
                loading={false}
              >
                Confirm
              </Button>
            </Col>
          </Row>
        </Card>
      </Form>
      <RemoveIntegrationModal
        isOpen={isRemoveIntegrationModalOpen}
        onCancel={() => setIsRemoveIntegrationModalOpen(false)}
        onOk={handleRemoveIntegration}
      />
    </Root>
  )
}

const Root = styled.div`
  > form {
    display: flex;
    gap: 24px;
    flex-direction: column;
  }
`

const Actions = styled.div<{ inactive?: boolean }>(
  ({ inactive }) => `
  display: flex;
  justify-content: flex-end;
  align-items: start;
  align-self: start;


  ${
    inactive &&
    `
      > * {
        opacity: 0.4;
        pointer-events: none;
      }
    `
  }
  `
)

const BusinessName = styled.span`
  color: #d092dd;
`

export default BusinessSection
